/**
 * Orchestrazione turni hot-seat.
 * Mantiene history, indice turno corrente, conteggio per entità.
 * Non sa nulla del gioco specifico: delega a GameModule.
 */

import type {
  GameConfig,
  GameModule,
  GuessRecord,
  GuessResult,
  Player,
  SerializedSession,
  TurnEntity,
} from './types';
import { entityName, pickPlayerForTurn } from './players';

export interface TurnSnapshot<TState> {
  entity: TurnEntity;
  entityLabel: string;
  player: Player;
  turnNumber: number; // 1-based
  state: TState;
  history: GuessRecord[];
}

export class TurnManager<TState, TGuess> {
  readonly gameType: string;
  readonly gameId: string;
  readonly config: GameConfig;
  private state: TState;
  private history: GuessRecord<TGuess>[] = [];
  private currentTurnIdx = 0;
  private readonly module: GameModule<TState, TGuess>;
  private readonly createdAt: number;

  constructor(args: {
    gameType: string;
    gameId: string;
    config: GameConfig;
    module: GameModule<TState, TGuess>;
    initialState?: TState;
    history?: GuessRecord<TGuess>[];
    currentTurnIdx?: number;
    createdAt?: number;
  }) {
    this.gameType = args.gameType;
    this.gameId = args.gameId;
    this.config = args.config;
    this.module = args.module;
    this.state = args.initialState ?? args.module.initState(args.config);
    this.history = args.history ?? [];
    this.currentTurnIdx = args.currentTurnIdx ?? 0;
    this.createdAt = args.createdAt ?? Date.now();
  }

  get currentEntity(): TurnEntity {
    const order = this.config.turnOrder;
    return order[this.currentTurnIdx % order.length];
  }

  /** Numero di turni passati per una specifica entità (per round-robin nelle squadre). */
  private turnsByEntity(entity: TurnEntity): number {
    return this.history.filter(
      (h) => h.byEntity.kind === entity.kind && h.byEntity.id === entity.id,
    ).length;
  }

  snapshot(): TurnSnapshot<TState> {
    const entity = this.currentEntity;
    const player = pickPlayerForTurn(entity, this.config, this.turnsByEntity(entity));
    return {
      entity,
      entityLabel: entityName(entity, this.config),
      player,
      turnNumber: this.history.length + 1,
      state: this.state,
      history: this.history as GuessRecord[],
    };
  }

  /** Esegue una guess e avanza il turno se non vinta. Restituisce il risultato. */
  submit(
    rawInput: string,
  ):
    | { ok: true; result: GuessResult; won: boolean }
    | { ok: false; reason: 'invalid' | 'duplicate' } {
    const guess = this.module.validateGuess(this.state, rawInput);
    if (guess === null) return { ok: false, reason: 'invalid' };
    if (this.module.isDuplicateGuess?.(this.state, guess))
      return { ok: false, reason: 'duplicate' };

    const entity = this.currentEntity;
    const player = pickPlayerForTurn(entity, this.config, this.turnsByEntity(entity));
    const { state: nextState, result } = this.module.applyGuess(
      this.state,
      guess,
      entity,
      player.id,
    );
    this.state = nextState;
    this.history.push({
      byEntity: entity,
      byPlayerId: player.id,
      guess,
      result,
      at: Date.now(),
    });

    const won = this.module.isWon(this.state);
    if (!won) this.currentTurnIdx += 1;
    return { ok: true, result, won };
  }

  /** Aggiorna lo stato senza creare un record nella history (es. rivelazione hint). */
  patchState(updater: (s: TState) => TState): void {
    this.state = updater(this.state);
  }

  /** Il giocatore si arrende. Lo stato viene marcato come gameOver. */
  surrender(): void {
    if (this.module.surrender) {
      this.state = this.module.surrender(this.state);
    }
  }

  isGameOver(): boolean {
    return this.module.isGameOver?.(this.state) ?? this.module.isWon(this.state);
  }

  isSurrendered(): boolean {
    return !this.module.isWon(this.state) && this.isGameOver();
  }

  getTargetLabel(): string | null {
    return this.module.getTargetLabel?.(this.state) ?? null;
  }

  serialize(): SerializedSession {
    const won = this.module.isWon(this.state);
    const over = this.isGameOver();
    return {
      gameId: this.gameId,
      gameType: this.gameType,
      config: this.config,
      state: this.module.serializeState(this.state),
      history: this.history as GuessRecord[],
      currentTurnIdx: this.currentTurnIdx,
      status: won ? 'won' : over ? 'surrendered' : 'active',
      createdAt: this.createdAt,
      updatedAt: Date.now(),
    };
  }

  static restore<TState, TGuess>(
    module: GameModule<TState, TGuess>,
    s: SerializedSession,
  ): TurnManager<TState, TGuess> {
    return new TurnManager({
      gameType: s.gameType,
      gameId: s.gameId.replace(/^.*::/, ''),
      config: s.config,
      module,
      initialState: module.deserializeState(s.state),
      history: s.history as GuessRecord<TGuess>[],
      currentTurnIdx: s.currentTurnIdx,
      createdAt: s.createdAt,
    });
  }
}
